import {DATE_FORMAT, TIME_FORMAT} from "../services/timezone-service";
import {DATETIME_VIEW} from "../components/datetime-v2/datetime-v2";
import currentPortalStore from "../stores/current-portal-store";
import TimezoneStore from "../stores/timezone-store";
import {FEATURES} from "../models/Plan";

/**
 * Default fallback formatting parameters.
 * Used when the TIMEZONES feature is disabled or timezone store is unavailable.
 * Exported for use in useDateTimeFormattingParams hook and components.
 */
export const FALLBACK_FORMAT_PARAMS = {
    timeZone: 'UTC',
    dateFormat: DATE_FORMAT.THIRD,
    timeFormat: TIME_FORMAT.AM_PM,
} as const;

/**
 * Convert database date string to UTC Date object.
 *
 * @param dbDateString - Date string from DB (e.g., "2025-01-04 10:30:00" or "2025-01-04T10:30:00Z")
 * @returns Date object with correct internal UTC timestamp
 *
 * @remarks
 * Prefer this method as it returns a Date with correct internal timestamp.
 * Automatically adds 'Z' suffix for strings without timezone indicator.
 */
export function convertSqlDateTimeStringToDate(dbDateString: string): Date {
    if (dbDateString.endsWith('Z')) {
        return new Date(dbDateString);
    }
    const isoString = dbDateString.replace(" ", "T") + "Z";
    return new Date(isoString);
}

/**
 * Extract date and time parts using Intl.DateTimeFormat in specified timezone.
 *
 * @param date - Date object to format
 * @param dateFormat - Merchant date format preference
 * @param timeFormat - Merchant time format preference (12h or 24h)
 * @param timeZone - IANA timezone string (e.g., "America/New_York")
 * @returns Object with formatted date/time parts
 */
function getDateParts(date: Date, dateFormat: DATE_FORMAT, timeFormat: TIME_FORMAT, timeZone: string) {
    const monthFormat = dateFormat === DATE_FORMAT.FIRST || dateFormat === DATE_FORMAT.SECOND ? '2-digit' : 'short';
    const dateParts = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: monthFormat,
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: timeFormat === TIME_FORMAT.TWENTY_FOUR ? 'h23' : 'h12',
        timeZone: timeZone
    }).formatToParts(date);

    const capitalizeFirstLetter = (str?: string) => {
        if (!str) {
            return str;
        }
        return (str + '').replace(/^\w/, (c) => c.toUpperCase());
    };

    const yearPart = dateParts.find(i => i.type === 'year')?.value;
    const monthPart = capitalizeFirstLetter(dateParts.find(i => i.type === 'month')?.value);
    const dayPart = dateParts.find(i => i.type === 'day')?.value;
    const hourPart = dateParts.find(i => i.type === 'hour')?.value;
    const minutePart = dateParts.find(i => i.type === 'minute')?.value;
    const periodPart = dateParts.find(i => i.type === "dayPeriod")?.value ?? '';

    return {yearPart, monthPart, dayPart, hourPart, minutePart, periodPart};
}

/**
 * Props for getFormattedDate function.
 */
export interface IGetFormattedDateProps {
    /** UTC date string to format */
    utcDateString: string;

    /** Date format to use */
    dateFormat: DATE_FORMAT;

    /** Time format to use */
    timeFormat: TIME_FORMAT;

    /** Timezone to convert to */
    timeZone: string;

    /** What parts to display */
    dateTimeView?: DATETIME_VIEW;
}

/**
 * Format date using timezone-aware mode with merchant settings.
 *
 * @param props - Date formatting configuration
 * @returns Formatted date string according to merchant settings
 */
export function getFormattedDate(
    {
        utcDateString,
        timeZone,
        dateTimeView = DATETIME_VIEW.FULL,
        timeFormat,
        dateFormat
    }: IGetFormattedDateProps): string
{

    if (!utcDateString) {
        return '';
    }

    const utcDate = convertSqlDateTimeStringToDate(utcDateString);

    if (isNaN(utcDate.getTime())) {
        return '';
    }

    const {
        yearPart,
        monthPart,
        dayPart,
        hourPart,
        minutePart,
        periodPart
    } = getDateParts(utcDate, dateFormat, timeFormat, timeZone);

    let dateFormatted = '';
    const timeFormatted = `${hourPart}:${minutePart}${periodPart ? ' ' + periodPart : ''}`;
    const showDay = [DATETIME_VIEW.DATE_ONLY, DATETIME_VIEW.FULL].includes(dateTimeView);

    if (dateFormat === DATE_FORMAT.FIRST) {
        if (showDay) {
            dateFormatted = `${dayPart}/${monthPart}/${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}/${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.SECOND) {
        if (showDay) {
            dateFormatted = `${monthPart}/${dayPart}/${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}/${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.THIRD) {
        if (showDay) {
            dateFormatted = `${dayPart} ${monthPart} ${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart} ${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.FOURTH) {
        if (showDay) {
            dateFormatted = `${monthPart} ${dayPart}, ${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}, ${yearPart}`;
        }
    }

    if (dateTimeView === DATETIME_VIEW.FULL) {
        return dateFormatted + ' ' + timeFormatted;
    }
    if (dateTimeView === DATETIME_VIEW.DATE_ONLY || dateTimeView === DATETIME_VIEW.MONTH_YEAR_ONLY) {
        return dateFormatted;
    }
    if (dateTimeView === DATETIME_VIEW.TIME_ONLY) {
        return timeFormatted;
    }

    return '';
}

/**
 * Format date string with automatic fallback to unified DATE_FORMAT.FIRST format.
 * Uses timezone store settings if TIMEZONES feature is enabled and tzStore is available,
 * otherwise falls back to unified DATE_FORMAT.FIRST format.
 * @param tzStore - TimezoneStore instance (can be null/undefined)
 * @param dateString - UTC date string to format (null returns '--')
 * @param dateTimeView - What parts to display (defaults to FULL)
 * @returns Formatted date string (merchant timezone if feature enabled and tzStore available, DATE_FORMAT.FIRST fallback otherwise)
 * @remarks
 * This utility encapsulates the common pattern of formatting dates with fallback.
 * It checks TIMEZONES feature availability and uses tzStore settings if available,
 * otherwise uses a unified DATE_FORMAT.FIRST format for consistency.
 */
export function formatDateTimeWithFallback(
    tzStore: TimezoneStore | null | undefined,
    dateString: string | null,
    dateTimeView: DATETIME_VIEW = DATETIME_VIEW.FULL
): string {
    if (dateString === null) {
        return '--';
    }

    if (tzStore) {
        return getFormattedDate({
            utcDateString: dateString,
            timeZone: tzStore.timezone,
            dateFormat: tzStore.dateFormat,
            timeFormat: tzStore.timeFormat,
            dateTimeView
        });
    }

    return getFormattedDate({
        utcDateString: dateString,
        timeZone: FALLBACK_FORMAT_PARAMS.timeZone,
        dateFormat: FALLBACK_FORMAT_PARAMS.dateFormat,
        timeFormat: FALLBACK_FORMAT_PARAMS.timeFormat,
        dateTimeView
    });
}

/**
 * Parse ISO date range string to IDateRangeISO object.
 * 
 * @param rangeString - Date range string in format "from/to" (e.g., "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * @param requireIsoFormat - If true, validates that dates contain 'T' (ISO format). Defaults to false.
 * @returns IDateRangeISO object with from and to dates, or undefined if invalid
 * 
 * @example
 * parseIsoDateRange("2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * // Returns: { from: "2025-01-01T00:00:00Z", to: "2025-12-31T23:59:59Z" }
 * 
 * @remarks
 * This utility consolidates the common pattern of parsing date range strings
 * stored in format "from/to" into structured objects for DateRangePickerV2.
 */
export function parseIsoDateRange(
    rangeString: string,
    requireIsoFormat: boolean = false
): { from: string; to: string } | undefined {
    if (!rangeString || !rangeString.includes('/')) {
        return undefined;
    }

    if (requireIsoFormat && !rangeString.includes('T')) {
        return undefined;
    }

    const [from, to] = rangeString.split('/');
    if (!from || !to) {
        return undefined;
    }

    return { from, to };
}

/**
 * Format ISO date range string for display with merchant timezone and format settings.
 * 
 * @param tzStore - TimezoneStore instance (can be null/undefined)
 * @param rangeString - Date range string in format "from/to" (e.g., "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * @param dateTimeView - What parts to display (defaults to DATE_ONLY)
 * @param separator - Separator between dates (defaults to " - ")
 * @returns Formatted date range string (e.g., "01 Jan 2025 - 31 Dec 2025") or original string if invalid
 * 
 * @example
 * formatIsoDateRange(tzStore, "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * // Returns: "01 Jan 2025 - 31 Dec 2025" (with merchant timezone/format)
 * 
 * @remarks
 * This utility consolidates the common pattern of formatting date range strings
 * for display in filter tags and other UI elements. It automatically handles
 * timezone conversion and format preferences.
 */
export function formatIsoDateRange(
    tzStore: TimezoneStore | null | undefined,
    rangeString: string,
    dateTimeView: DATETIME_VIEW = DATETIME_VIEW.DATE_ONLY,
    separator: string = ' - '
): string {
    const parsed = parseIsoDateRange(rangeString);
    if (!parsed) {
        return rangeString;
    }

    const fromFormatted = formatDateTimeWithFallback(tzStore, parsed.from, dateTimeView);
    const toFormatted = formatDateTimeWithFallback(tzStore, parsed.to, dateTimeView);

    return `${fromFormatted}${separator}${toFormatted}`;
}